import { base64UrlToUtf8String } from './base64UrlUtf8';

const TOKEN_KEY = 'pracadigital_auth_token';

interface CookieOptions {
  expires?: Date | number;
  maxAge?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}
const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const {
    expires,
    maxAge,
    path = '/',
    domain,
    secure = window.location.protocol === 'https:',
    sameSite = 'Lax',
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires) {
    if (expires instanceof Date) {
      cookieString += `; expires=${expires.toUTCString()}`;
    } else {
      const date = new Date();
      date.setTime(date.getTime() + expires * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }
  }

  if (maxAge) {
    cookieString += `; max-age=${maxAge}`;
  }

  cookieString += `; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  cookieString += `; samesite=${sameSite}`;

  try {
    document.cookie = cookieString;
  } catch (error) {
    console.error('Erro ao definir cookie:', error);
    throw new Error('Falha ao armazenar token de autenticação');
  }
};

const getCookie = (name: string): string | null => {
  try {
    const nameEQ = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
      }
    }
    return null;
  } catch (error) {
    console.error('Erro ao recuperar cookie:', error);
    return null;
  }
};

const deleteCookie = (name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void => {
  const { path = '/', domain } = options;

  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  try {
    document.cookie = cookieString;
  } catch (error) {
    console.error('Erro ao remover cookie:', error);
  }
};

const getTokenExpiration = (token: string): Date | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(base64UrlToUtf8String(parts[1]));

    if (payload.exp) {
      return new Date(payload.exp * 1000);
    }

    return null;
  } catch {
    return null;
  }
};

export type SetAccessTokenOptions = {
  /** When false, token is stored as a session cookie (cleared when the browser closes). */
  rememberMe?: boolean;
};

export const setAccessToken = (token: string, options: SetAccessTokenOptions = {}): void => {
  try {
    const rememberMe = options.rememberMe ?? true;

    const cookieOptions: CookieOptions = {
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
      path: '/',
    };

    if (rememberMe) {
      const expiration = getTokenExpiration(token);

      if (expiration) {
        cookieOptions.expires = expiration;
      } else {
        cookieOptions.maxAge = 7 * 24 * 60 * 60;
      }
    }

    setCookie(TOKEN_KEY, token, cookieOptions);
  } catch (error) {
    console.error('Erro ao armazenar token:', error);
    throw new Error('Falha ao armazenar token de autenticação');
  }
};

export const getAccessToken = (): string | null => {
  try {
    return getCookie(TOKEN_KEY);
  } catch (error) {
    console.error('Erro ao recuperar token:', error);
    return null;
  }
};

export const clearTokens = (): void => {
  try {
    deleteCookie(TOKEN_KEY, { path: '/' });
  } catch (error) {
    console.error('Erro ao limpar tokens:', error);
  }
};

export const hasToken = (): boolean => {
  return getAccessToken() !== null;
};
